import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Check, ChevronRight, Search } from '@/components/ui/icons';
import { Popover } from './Popover';

export function Menu({ children, className, style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={clsx('menu', className)} role="menu" style={style}>
      {children}
    </div>
  );
}

export interface MenuItemProps {
  icon?: ReactNode;
  label: ReactNode;
  hint?: ReactNode;
  shortcut?: string;
  danger?: boolean;
  selected?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  right?: ReactNode;
  submenu?: (close: () => void) => ReactNode;
  active?: boolean;
  className?: string;
}

export function MenuItem({ icon, label, hint, shortcut, danger, selected, checked, disabled, onClick, right, submenu, active, className }: MenuItemProps) {
  const body = (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={clsx('menu-item', { danger, selected, active }, className)}
      onClick={submenu ? undefined : onClick}
    >
      {icon !== undefined && <span className="menu-item-icon">{icon}</span>}
      <span className="menu-item-label truncate">{label}</span>
      {hint && <span className="menu-item-hint truncate">{hint}</span>}
      {right}
      {shortcut && <span className="menu-item-shortcut">{shortcut}</span>}
      {checked !== undefined && <span className="menu-item-check">{checked && <Check size={14} />}</span>}
      {submenu && <ChevronRight size={14} className="menu-item-chevron" />}
    </button>
  );
  if (!submenu) return body;
  return (
    <Popover placement="right-start" offsetPx={2} content={submenu}>
      {body}
    </Popover>
  );
}

export function MenuSeparator() {
  return <div className="menu-sep" role="separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="menu-label">{children}</div>;
}

export function MenuSearch({
  value,
  onChange,
  placeholder = 'Search...',
  onEnter,
  autoFocus = true,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) requestAnimationFrame(() => ref.current?.focus());
  }, [autoFocus]);
  return (
    <div className="menu-search">
      <Search size={14} className="faint" />
      <input
        ref={ref}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onEnter?.();
        }}
      />
    </div>
  );
}

/** Keyboard navigation for a flat list of options inside a picker. */
export function useListNav(count: number, onPick: (index: number) => void) {
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [count]);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => Math.min(count - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (count > 0) onPick(index);
    }
  };
  return { index, setIndex, onKeyDown };
}
